// Admin credits — purchase log + refund action.
//
// Operators review credit purchases here. The primary action is
// "Refund": fires a Stripe refund (server side via /admin/credit-
// purchases/:id/refund), then the existing Stripe `charge.refunded`
// webhook does the actual balance debit. Single source of truth.
import { useCallback, useEffect, useState } from "react";
import { Button } from "../../components/ui/Button";
import { Card, CardSubtle, CardTitle } from "../../components/ui/Card";
import { Input, Label, Select, Textarea } from "../../components/ui/Input";
import { Modal } from "../../components/ui/Modal";
import { Spinner } from "../../components/ui/Spinner";
import { api } from "../../lib/api";

interface CreditPurchaseRow {
  id: string;
  user_id: string;
  amount_usd: number;
  amount_original: number;
  currency_original: string;
  payment_provider: string;
  external_payment_id: string;
  status: string;
  refunded_at: string | null;
  refund_reason: string | null;
  created_at: string;
  completed_at: string | null;
  profiles: {
    id: string;
    username: string;
    avatar_url: string | null;
    country_code: string | null;
    platform_status: string;
  } | null;
}

interface Stats {
  completed: { count: number; sum: number };
  pending: { count: number; sum: number };
  refunded: { count: number; sum: number };
}

interface ListResponse {
  purchases: CreditPurchaseRow[];
  total: number;
  stats: Stats;
}

const STATUS_OPTIONS = [
  { value: "", label: "All statuses" },
  { value: "completed", label: "Completed" },
  { value: "pending", label: "Pending" },
  { value: "failed", label: "Failed" },
  { value: "refunded", label: "Refunded" },
];

function formatUsd(n: number): string {
  return `$${n.toFixed(2)}`;
}

function formatDate(iso: string): string {
  return new Date(iso).toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

function statusColor(status: string): string {
  switch (status) {
    case "completed":
      return "var(--upvote, #22C55E)";
    case "failed":
    case "refunded":
      return "var(--danger, #ef4444)";
    case "pending":
      return "var(--accent)";
    default:
      return "var(--text-secondary)";
  }
}

export default function AdminCreditsPage() {
  const [data, setData] = useState<ListResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [statusFilter, setStatusFilter] = useState("");
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");

  const [refundTarget, setRefundTarget] = useState<CreditPurchaseRow | null>(
    null,
  );
  const [refundReason, setRefundReason] = useState("");
  const [refundSubmitting, setRefundSubmitting] = useState(false);
  const [refundError, setRefundError] = useState<string | null>(null);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search), 300);
    return () => clearTimeout(t);
  }, [search]);

  const fetchList = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams();
      if (statusFilter) params.set("status", statusFilter);
      if (debouncedSearch) params.set("search", debouncedSearch);
      params.set("limit", "100");
      const res = await api<ListResponse>(
        `/admin/credit-purchases?${params.toString()}`,
      );
      setData(res);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load");
    } finally {
      setLoading(false);
    }
  }, [statusFilter, debouncedSearch]);

  useEffect(() => {
    fetchList();
  }, [fetchList]);

  async function submitRefund() {
    if (!refundTarget) return;
    setRefundSubmitting(true);
    setRefundError(null);
    try {
      await api(`/admin/credit-purchases/${refundTarget.id}/refund`, {
        method: "POST",
        body: { reason: refundReason.trim() },
      });
      setRefundTarget(null);
      setRefundReason("");
      await fetchList();
    } catch (err) {
      setRefundError(err instanceof Error ? err.message : "Refund failed");
    } finally {
      setRefundSubmitting(false);
    }
  }

  const rows = data?.purchases ?? [];

  return (
    <div>
      <div style={styles.header}>
        <h1 style={styles.h1}>Credits</h1>
        <CardSubtle>
          Credit purchases. Refunds fire a Stripe refund; the webhook
          does the balance debit when Stripe confirms.
        </CardSubtle>
      </div>

      <div style={styles.statsRow}>
        <Card style={{ flex: 1 }}>
          <CardSubtle>Completed (all-time)</CardSubtle>
          <div style={styles.statValue}>
            {data?.stats.completed.count ?? "—"}
          </div>
          <CardSubtle>
            {data ? formatUsd(data.stats.completed.sum) : ""}
          </CardSubtle>
        </Card>
        <Card style={{ flex: 1 }}>
          <CardSubtle>Pending</CardSubtle>
          <div style={styles.statValue}>
            {data?.stats.pending.count ?? "—"}
          </div>
          <CardSubtle>
            {data ? formatUsd(data.stats.pending.sum) : ""}
          </CardSubtle>
        </Card>
        <Card style={{ flex: 1 }}>
          <CardSubtle>Refunded</CardSubtle>
          <div style={styles.statValue}>
            {data?.stats.refunded.count ?? "—"}
          </div>
          <CardSubtle>
            {data ? formatUsd(data.stats.refunded.sum) : ""}
          </CardSubtle>
        </Card>
      </div>

      <Card style={{ marginBottom: 16 }}>
        <div style={styles.filterRow}>
          <div style={{ flex: 1 }}>
            <Label>Status</Label>
            <Select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
            >
              {STATUS_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </Select>
          </div>
          <div style={{ flex: 2 }}>
            <Label>Search user</Label>
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="username substring"
            />
          </div>
        </div>
      </Card>

      {loading ? (
        <Card>
          <div style={styles.loadingWrap}>
            <Spinner />
          </div>
        </Card>
      ) : error ? (
        <Card>
          <div style={styles.errorBar}>{error}</div>
        </Card>
      ) : rows.length === 0 ? (
        <Card>
          <CardTitle>No purchases</CardTitle>
          <CardSubtle>Nothing matches the current filters.</CardSubtle>
        </Card>
      ) : (
        <Card padding={0}>
          <table style={styles.table}>
            <thead>
              <tr style={styles.tableHead}>
                <Th>User</Th>
                <Th>Amount</Th>
                <Th>Provider</Th>
                <Th>Status</Th>
                <Th>Created</Th>
                <Th>Actions</Th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={row.id} style={styles.tableRow}>
                  <Td>
                    <div style={styles.userCell}>
                      <span>@{row.profiles?.username ?? "(deleted)"}</span>
                      {row.profiles?.country_code && (
                        <CardSubtle style={{ fontSize: 12 }}>
                          {row.profiles.country_code}
                        </CardSubtle>
                      )}
                    </div>
                  </Td>
                  <Td>
                    <strong>{formatUsd(row.amount_usd)}</strong>
                    {row.currency_original !== "USD" && (
                      <CardSubtle style={{ fontSize: 12 }}>
                        {row.amount_original.toFixed(2)} {row.currency_original}
                      </CardSubtle>
                    )}
                  </Td>
                  <Td>{row.payment_provider}</Td>
                  <Td>
                    <span style={{ color: statusColor(row.status) }}>
                      {row.status}
                    </span>
                    {row.refund_reason && (
                      <CardSubtle
                        style={{
                          fontSize: 12,
                          fontStyle: "italic",
                          marginTop: 2,
                        }}
                      >
                        {row.refund_reason}
                      </CardSubtle>
                    )}
                  </Td>
                  <Td>
                    <CardSubtle style={{ fontSize: 12 }}>
                      {formatDate(row.created_at)}
                    </CardSubtle>
                  </Td>
                  <Td>
                    {row.status === "completed" && (
                      <Button
                        variant="danger"
                        size="sm"
                        onClick={() => setRefundTarget(row)}
                      >
                        Refund
                      </Button>
                    )}
                  </Td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      )}

      <Modal
        open={refundTarget !== null}
        onClose={() => !refundSubmitting && setRefundTarget(null)}
        title={
          refundTarget
            ? `Refund ${formatUsd(refundTarget.amount_usd)} to @${refundTarget.profiles?.username ?? ""}`
            : ""
        }
      >
        <CardSubtle style={{ marginBottom: 12 }}>
          Fires a Stripe refund. The balance debit happens when Stripe
          confirms via the charge.refunded webhook. If the user has
          already spent some of these credits, the balance will clamp
          to zero — manual reconcile may be needed.
        </CardSubtle>
        <Label>Reason</Label>
        <Textarea
          value={refundReason}
          onChange={(e) => setRefundReason(e.target.value)}
          rows={3}
          placeholder="Briefly explain why this refund is happening"
        />
        {refundError && (
          <div style={{ ...styles.errorBar, marginTop: 12 }}>
            {refundError}
          </div>
        )}
        <div style={{ marginTop: 16, display: "flex", gap: 8 }}>
          <Button
            variant="secondary"
            onClick={() => setRefundTarget(null)}
            disabled={refundSubmitting}
          >
            Cancel
          </Button>
          <Button
            variant="danger"
            onClick={submitRefund}
            disabled={refundReason.trim().length < 3 || refundSubmitting}
          >
            {refundSubmitting ? "Refunding…" : "Confirm refund"}
          </Button>
        </div>
      </Modal>
    </div>
  );
}

function Th({ children }: { children: React.ReactNode }) {
  return <th style={styles.th}>{children}</th>;
}
function Td({ children }: { children: React.ReactNode }) {
  return <td style={styles.td}>{children}</td>;
}

const styles: Record<string, React.CSSProperties> = {
  header: { marginBottom: 16 },
  h1: { margin: 0, fontSize: 24, fontWeight: 700, color: "var(--text-primary)" },
  statsRow: { display: "flex", gap: 12, marginBottom: 16 },
  statValue: {
    fontSize: 28,
    fontWeight: 700,
    color: "var(--text-primary)",
    margin: "4px 0",
  },
  filterRow: { display: "flex", gap: 12 },
  loadingWrap: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: "48px 0",
  },
  errorBar: {
    background: "rgba(239,68,68,0.1)",
    color: "var(--danger, #ef4444)",
    padding: 12,
    borderRadius: "var(--radius-sm)",
  },
  table: {
    width: "100%",
    borderCollapse: "collapse",
    fontSize: 14,
  },
  tableHead: {
    background: "rgba(255,255,255,0.02)",
    textAlign: "left",
  },
  tableRow: { borderTop: "1px solid var(--border)" },
  th: {
    padding: "10px 12px",
    fontSize: 12,
    fontWeight: 600,
    color: "var(--text-secondary)",
    textTransform: "uppercase",
    letterSpacing: 0.3,
  },
  td: { padding: 12, verticalAlign: "top", color: "var(--text-primary)" },
  userCell: { display: "flex", flexDirection: "column", gap: 2 },
};
